import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail } from '../../utils/commonUtils';
import { ShopFormUtils } from '../../utils/shopCommon';
import { 
  Home,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  FolderTree
} from 'lucide-react';

// 그룹 인터페이스 (임시 - 실제 API 구조에 맞게 수정 필요)
interface ShopGroup {
  code: string;
  name: string;
  number: number;
  childGroupCount: number;
  shopCount: number;
  createdAt: string;
}

interface ShopGroupListRequest {
  page?: number;
  size?: number;
  sort?: string;
  search?: string;
}

interface ShopGroupListResponse {
  totalCount: number;
  groups: ShopGroup[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const ShopGroupList: React.FC = () => {
  // Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('group-list');

  // List state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // URL 파라미터에서 페이징 정보 읽기
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const page = searchParams.get('page');
    const size = searchParams.get('size');
    
    if (page !== null) {
      setCurrentPage(parseInt(page, 10));
    }
    if (size !== null) {
      setPageSize(parseInt(size, 10));
    }
  }, [location.search]);

  const [sortField, setSortField] = useState('number');
  const [sortOrder, setSortOrder] = useState('desc');
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // API state
  const [groups, setGroups] = useState<ShopGroup[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);

  // Check if screen is mobile size
  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 그룹 목록 조회 (임시 - 실제 API 함수로 교체 필요)
  useEffect(() => {
    const fetchGroups = async () => {
      if (!projectDetail) {
        console.log('프로젝트 정보가 없습니다.');
        return;
      }
      
      try {
        setIsLoading(true);
        setError(null);
        
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          throw new Error('프로젝트 코드가 없습니다.');
        }
        
        // TODO: 실제 API 호출로 교체
        // const request: ShopGroupListRequest = {
        //   page: currentPage,
        //   size: pageSize,
        //   sort: `${sortField},${sortOrder}`
        // };
        // const response = await getShopGroupList(projectCode, request);
        
        // 임시 데이터
        await new Promise(resolve => setTimeout(resolve, 500)); // 로딩 시뮬레이션
        const mockGroups: ShopGroup[] = [];
        const mockResponse: ShopGroupListResponse = {
          totalCount: 0,
          groups: mockGroups,
          totalPages: 0,
          currentPage: 0,
          pageSize: 20,
          hasNext: false,
          hasPrevious: false
        };
        
        setGroups(mockResponse.groups);
        setTotalCount(mockResponse.totalCount);
        if (mockResponse.pageSize && mockResponse.pageSize !== pageSize) {
          setPageSize(mockResponse.pageSize);
        }
      } catch (err) {
        console.error('그룹 목록 조회 오류:', err);
        setError(err instanceof Error ? err.message : '그룹 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        setIsLoading(false);
      }
    };

    if (projectDetail) {
      fetchGroups();
    }
  }, [projectDetail, currentPage, pageSize, sortField, sortOrder]);

  // 날짜 포맷팅 함수
  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      return ShopFormUtils.formatDate(dateString);
    } catch (error) {
      console.error('날짜 포맷팅 오류:', error);
      return dateString.split(' ')[0];
    }
  };

  // Sort function
  const handleSort = (field: string) => {
    ShopFormUtils.handleSort(field, sortField, sortOrder, setSortField, setSortOrder);
  };

  // Select all handler
  const handleSelectAll = () => {
    if (selectedGroups.length === filteredGroups.length && filteredGroups.length > 0) {
      setSelectedGroups([]);
    } else {
      setSelectedGroups(filteredGroups.map(group => group.code));
    }
  };

  // Select group handler
  const handleSelectGroup = (groupCode: string) => {
    if (selectedGroups.includes(groupCode)) {
      setSelectedGroups(selectedGroups.filter(code => code !== groupCode));
    } else {
      setSelectedGroups([...selectedGroups, groupCode]);
    }
  };

  // Page change handler
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    const params = new URLSearchParams(location.search);
    params.set('page', page.toString());
    navigate({ search: params.toString() });
  };

  // Page size change handler
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
    const params = new URLSearchParams(location.search);
    params.set('size', size.toString());
    params.set('page', '0');
    navigate({ search: params.toString() });
  };

  // Filter groups
  const filteredGroups: ShopGroup[] = groups.filter(group => {
    const matchesSearch = group.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         group.code.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // 프로젝트 정보가 없거나 에러가 있을 때 처리
  if (!projectDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          {error ? (
            <>
              <div className="text-red-500 mb-4">
                <Home className="w-12 h-12 mx-auto" />
              </div>
              <p className="text-red-600 mb-4 text-center max-w-md">{error}</p>
              <button
                onClick={() => {
                  setError(null);
                  window.location.reload();
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                새로고침
              </button>
            </>
          ) : (
            <>
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600">프로젝트 정보를 불러오는 중...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {/* Sidebar */}
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar
          title="매장 관리"
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail}
        />

        {/* Group List Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-full">
            {/* Filters and Actions */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
                <div className="flex flex-col sm:flex-row gap-3 flex-1 w-full">
                  {/* Search */}
                  <div className="relative flex-1 sm:max-w-xs min-w-[120px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="그룹명, 코드로 검색"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div className="flex gap-2 w-full sm:w-auto">
                  {/* Excel Download */}
                  <button
                    onClick={() => alert('엑셀 다운로드 기능은 준비중입니다.')}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    엑셀
                  </button>

                  {/* Add Group Button */}
                  <button 
                    onClick={() => alert('그룹 추가 기능은 준비중입니다.')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Plus className="w-5 h-5 mr-2" />
                    그룹 추가
                  </button>
                </div>
              </div>

              {/* Summary */}
              <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  총 <span className="font-medium text-gray-900">{totalCount}</span>개 그룹 / 
                  <span className="ml-1">검색결과 <span className="font-medium text-gray-900">{filteredGroups.length}</span>개</span>
                </p>
              </div>
            </div>

            {/* Error State */}
            {error && (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="text-red-500 mb-4">
                  <Home className="w-12 h-12 mx-auto" />
                </div>
                <p className="text-red-600 mb-4 text-center max-w-md mx-auto">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    window.location.reload();
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  다시 시도
                </button>
              </div>
            )}

            {/* Table View */}
            {!error && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="pl-4 pr-0 py-3 text-left">
                          <input
                            type="checkbox"
                            checked={selectedGroups.length === filteredGroups.length && filteredGroups.length > 0}
                            onChange={handleSelectAll}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '80px', width: '80px' }}>
                          <button
                            onClick={() => handleSort('number')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700 whitespace-nowrap"
                          >
                            번호
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <button
                            onClick={() => handleSort('code')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            그룹 코드
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left" style={{ minWidth: '200px' }}>
                          <button
                            onClick={() => handleSort('name')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            그룹명
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                            하위 그룹수
                          </span>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                            하위 매장수
                          </span>
                        </th>
                        <th className="pl-6 pr-0 py-3 text-left">
                          <button
                            onClick={() => handleSort('createdAt')}
                            className="group inline-flex items-center text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700"
                          >
                            생성일
                            <ArrowUpDown className="ml-1 h-3 w-3" />
                          </button>
                        </th>
                        <th className="relative px-6 py-3">
                          <span className="sr-only">작업</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center">
                            <div className="flex justify-center">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            </div>
                          </td>
                        </tr>
                      ) : filteredGroups.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <FolderTree className="w-8 h-8 text-gray-400 mb-2" />
                              <p className="text-sm text-gray-500">등록된 그룹이 없습니다.</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredGroups.map((group) => (
                          <tr key={group.code} className="hover:bg-gray-50">
                            <td className="pl-4 pr-0 py-4 whitespace-nowrap">
                              <input
                                type="checkbox"
                                checked={selectedGroups.includes(group.code)}
                                onChange={() => handleSelectGroup(group.code)}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm font-medium text-gray-900" style={{ minWidth: '80px', width: '80px' }}>
                              #{group.number}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                              {group.code}
                            </td>
                            <td className="pl-6 pr-0 py-4" style={{ minWidth: '200px' }}>
                              <div className="text-sm font-medium text-gray-900 truncate">{group.name}</div>
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                              {group.childGroupCount}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                              {group.shopCount}
                            </td>
                            <td className="pl-6 pr-0 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(group.createdAt)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  className="text-gray-400 hover:text-gray-600"
                                  title="상세 보기"
                                  onClick={() => alert('상세 보기 기능은 준비중입니다.')}
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-gray-400 hover:text-blue-600"
                                  title="수정"
                                  onClick={() => alert('수정 기능은 준비중입니다.')}
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  className="text-gray-400 hover:text-red-600"
                                  title="삭제"
                                  onClick={() => alert('삭제 기능은 준비중입니다.')}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Selected Actions Bar */}
                {selectedGroups.length > 0 && (
                  <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">
                        {selectedGroups.length}개 그룹 선택됨
                      </span>
                      <div className="flex items-center space-x-2">
                        <button className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50">
                          엑셀 다운로드
                        </button>
                        <button className="px-3 py-1 text-sm bg-red-600 text-white rounded hover:bg-red-700">
                          선택 삭제
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Pagination */}
            {totalCount > 0 && (
              <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700">페이지당</span>
                    <select 
                      value={pageSize}
                      onChange={(e) => {
                        handlePageSizeChange(parseInt(e.target.value));
                      }}
                      className="px-1 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="10">10개</option>
                      <option value="20">20개</option>
                      <option value="50">50개</option>
                      <option value="100">100개</option>
                    </select>
                    <span>표시</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handlePageChange(0)}
                      disabled={currentPage === 0}
                      className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      처음
                    </button>
                    <button
                      onClick={() => handlePageChange(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    
                    {/* Page Numbers */}
                    {Array.from({ length: Math.ceil(totalCount / pageSize) }, (_, i) => i).slice(0, 5).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3 py-1 rounded text-sm ${
                          currentPage === page
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {page + 1}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(Math.min(Math.ceil(totalCount / pageSize) - 1, currentPage + 1))}
                      disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                      className="px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.ceil(totalCount / pageSize) - 1)}
                      disabled={currentPage >= Math.ceil(totalCount / pageSize) - 1}
                      className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      마지막
                    </button>
                  </div>

                  <p className="text-sm text-gray-600">
                    {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalCount)} / 총 {totalCount}개
                  </p>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default ShopGroupList;